import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '../../../store/store';
import { addEntity, selectIsConversationBusy } from '../../timeline/state/timelineSlice';
import { CreateRunError, createRun } from '../api/sseClient';
import type { ChatMessageEntrypoint } from '../messageEntrypoint';

export interface EnqueueOptions {
  convId: string;
  profile: string;
  text: string;
  draftBundleId?: string; // Optional UUID of draft bundle to use for prompt resolution
  context?: any;
  clientMessageId?: string;
  messageEntrypoint: ChatMessageEntrypoint;
}

interface QueuedItem extends EnqueueOptions {
  id: string;
  enqueuedAt: number;
  retryCount: number;
}

/** Run created by the queue that still needs an SSE stream opened by the hook. */
export interface PendingStreamRun {
  runId: string;
  serverConvId: string;
}

interface QueueStatePerConv {
  queue: QueuedItem[];
  processing: boolean;
  /** Set after a queued createRun succeeds so the chat hook can open SSE for it. */
  pendingStreamRun: PendingStreamRun | null;
}

interface ChatQueueState {
  byConvId: Record<string, QueueStatePerConv>;
}

const initialState: ChatQueueState = {
  byConvId: {},
};

const chatQueueSlice = createSlice({
  name: 'chatQueue',
  initialState,
  reducers: {
    enqueue(state, action: PayloadAction<EnqueueOptions>) {
      const { convId } = action.payload;
      if (!state.byConvId[convId]) {
        state.byConvId[convId] = { queue: [], processing: false, pendingStreamRun: null };
      }
      const q = state.byConvId[convId].queue;
      q.push({
        ...action.payload,
        id: `q-${Date.now()}-${q.length + 1}`,
        enqueuedAt: Date.now(),
        retryCount: 0,
      });
    },
    dequeue(state, action: PayloadAction<{ convId: string }>) {
      const { convId } = action.payload;
      const bucket = state.byConvId[convId];
      if (!bucket) return;
      bucket.queue.shift();
    },
    setProcessing(state, action: PayloadAction<{ convId: string; processing: boolean }>) {
      const { convId, processing } = action.payload;
      if (!state.byConvId[convId]) {
        state.byConvId[convId] = { queue: [], processing: false, pendingStreamRun: null };
      }
      state.byConvId[convId].processing = processing;
    },
    bumpRetry(state, action: PayloadAction<{ convId: string }>) {
      const { convId } = action.payload;
      const bucket = state.byConvId[convId];
      if (!bucket || bucket.queue.length === 0) return;
      bucket.queue[0].retryCount += 1;
    },
    clearQueue(state, action: PayloadAction<{ convId: string }>) {
      const { convId } = action.payload;
      if (state.byConvId[convId]) {
        state.byConvId[convId].queue = [];
        state.byConvId[convId].processing = false;
      }
    },
    /** Store a queued run result so the chat hook can open an SSE stream for it. */
    setPendingStreamRun(
      state,
      action: PayloadAction<{ convId: string; runId: string; serverConvId: string }>
    ) {
      const { convId, runId, serverConvId } = action.payload;
      if (!state.byConvId[convId]) {
        state.byConvId[convId] = { queue: [], processing: false, pendingStreamRun: null };
      }
      state.byConvId[convId].pendingStreamRun = { runId, serverConvId };
    },
    /** Clear after the hook has consumed the pending run. */
    clearPendingStreamRun(state, action: PayloadAction<{ convId: string }>) {
      const bucket = state.byConvId[action.payload.convId];
      if (bucket) {
        bucket.pendingStreamRun = null;
      }
    },
  },
});

export const {
  enqueue,
  dequeue,
  setProcessing,
  bumpRetry,
  clearQueue,
  setPendingStreamRun,
  clearPendingStreamRun,
} = chatQueueSlice.actions;
export default chatQueueSlice.reducer;

// Internal retry timers per conversation
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function schedule(convId: string, delayMs: number, dispatch: AppDispatch) {
  const existing = timers.get(convId);
  if (existing) clearTimeout(existing);
  const t = setTimeout(() => {
    dispatch(processQueue(convId));
  }, delayMs);
  timers.set(convId, t);
}

function isRetryableConflictError(err: unknown): boolean {
  if (err instanceof CreateRunError) {
    if (err.status !== 409) {
      return false;
    }
    const message = err.message.toLowerCase();
    return (
      message.includes('conversation is busy') && !message.includes('idempotency key conflicts')
    );
  }

  const anyErr = err as any;
  const isConflict =
    anyErr?.status === 409 ||
    anyErr?.statusCode === 409 ||
    anyErr?.response?.status === 409 ||
    anyErr?.response?.statusCode === 409 ||
    (typeof anyErr?.code === 'string' && anyErr.code.toUpperCase().includes('CONFLICT'));
  if (!isConflict) {
    return false;
  }

  const message = String(
    anyErr?.data?.error ?? anyErr?.error ?? anyErr?.message ?? anyErr?.response?.data?.error ?? ''
  ).toLowerCase();
  return message.includes('conversation is busy') && !message.includes('idempotency key conflicts');
}

function findRecentTimelineErrorMessage(
  state: RootState,
  convId: string,
  since: number
): string | null {
  const timeline = state.timeline?.byConvId?.[convId];
  if (!timeline) {
    return null;
  }

  const recentErrors = timeline.order
    .map((id) => timeline.byId[id])
    .filter((entity) => {
      if (entity?.kind !== 'message') return false;
      const props = entity.props as any;
      if (props?.role !== 'system' || props?.severity !== 'error') return false;
      return entity.timestamp >= since;
    });

  if (recentErrors.length === 0) {
    return null;
  }

  const errorProps = recentErrors[recentErrors.length - 1].props as any;
  const content = errorProps?.content;
  if (typeof content === 'string' && content.trim()) {
    return content.replace(/^⚠️\s*/, '');
  }

  return 'Failed to send message';
}

export const enqueueChatMessage = (opts: EnqueueOptions) => (dispatch: AppDispatch) => {
  dispatch(enqueue(opts));
  dispatch(processQueue(opts.convId));
};

/**
 * Enqueues a chat message and returns a promise that resolves on success or rejects on error.
 * Use this when you need to know if the send succeeded (e.g., to conditionally remove UI elements).
 */
export const enqueueChatMessageAndWait =
  (opts: EnqueueOptions) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const timestampBefore = Date.now();

    dispatch(enqueue(opts));

    // Get the queue after enqueuing - the new item is the last one (since push adds to end)
    const queueAfterEnqueue = selectQueue(opts.convId)(getState());
    const enqueuedItem = queueAfterEnqueue[queueAfterEnqueue.length - 1];

    if (!enqueuedItem) {
      throw new Error('Failed to enqueue message - item not found in queue');
    }

    const enqueuedItemId = enqueuedItem.id;
    dispatch(processQueue(opts.convId));

    // Wait for the specific item to be processed
    // Poll until the item with our ID is dequeued (success) or an error occurs
    const maxWaitMs = 30000; // 30 seconds max wait
    const pollIntervalMs = 100;
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      const state = getState();
      const queueAfter = selectQueue(opts.convId)(state);

      // Check if our specific item is still in the queue
      const itemStillInQueue = queueAfter.some((item) => item.id === enqueuedItemId);

      const errorMessage = findRecentTimelineErrorMessage(state, opts.convId, timestampBefore);
      if (errorMessage) {
        throw new Error(errorMessage);
      }

      // If our specific item is no longer in the queue, it was successfully dequeued
      if (!itemStillInQueue) {
        return; // Success
      }

      // If not processing and item is still in queue after reasonable time, might be stuck
      const isProcessing = selectIsProcessing(opts.convId)(state);
      const elapsed = Date.now() - startTime;
      if (!isProcessing && itemStillInQueue && elapsed > 5000) {
        // Item might be stuck (e.g., waiting for busy state that never clears)
        // Check one more time for errors, then timeout
        await new Promise((resolve) => setTimeout(resolve, 500));
        const finalState = getState();
        const finalErrorMessage = findRecentTimelineErrorMessage(
          finalState,
          opts.convId,
          timestampBefore
        );
        if (finalErrorMessage) {
          throw new Error(finalErrorMessage);
        }
        const finalQueue = selectQueue(opts.convId)(finalState);
        const stillInQueue = finalQueue.some((item) => item.id === enqueuedItemId);
        if (!stillInQueue) {
          return; // Success after delay
        }
        throw new Error('Request timed out - queue item was not processed');
      }
    }

    // Timeout - check one final time
    const finalState = getState();
    const finalErrorMessage = findRecentTimelineErrorMessage(
      finalState,
      opts.convId,
      timestampBefore
    );
    if (finalErrorMessage) {
      throw new Error(finalErrorMessage);
    }
    const finalQueue = selectQueue(opts.convId)(finalState);
    const stillInQueue = finalQueue.some((item) => item.id === enqueuedItemId);
    if (!stillInQueue) {
      return; // Success
    }
    throw new Error('Request timed out');
  };

export const processQueue =
  (convId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const bucket = state.chatQueue?.byConvId?.[convId];
    if (!bucket) return;

    if (bucket.processing) return;

    const isBusy = selectIsConversationBusy(convId)(state);
    if (isBusy) {
      schedule(convId, 250, dispatch);
      return;
    }

    const item = bucket.queue[0];
    if (!item) return;

    dispatch(setProcessing({ convId, processing: true }));
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const clientTime = new Date().toISOString();
      const { profile, text, draftBundleId, context, clientMessageId, messageEntrypoint } = item;

      // Use stateless createRun API (SSE-based, no WebSocket fallback)
      const result = await createRun({
        conversationId: convId,
        prompt: text,
        profileSlug: profile || undefined, // empty → let server use conversation's existing profile
        messageEntrypoint,
        timezone,
        clientTime,
        clientMessageId: clientMessageId || item.id,
        draftBundleId,
        context,
      });

      // Signal the chat hook to open an SSE stream for this run.
      // Without this the run executes server-side but SEM events are never
      // consumed by the client (since the old persistent WebSocket was removed).
      dispatch(
        setPendingStreamRun({
          convId,
          runId: result.runId,
          serverConvId: result.conversationId,
        })
      );

      // Success: remove from queue
      dispatch(dequeue({ convId }));
      dispatch(setProcessing({ convId, processing: false }));
      // Immediately try next item
      dispatch(processQueue(convId));
      return result;
    } catch (err) {
      dispatch(setProcessing({ convId, processing: false }));
      if (isRetryableConflictError(err)) {
        // Treat conflict as transient due to in-flight work; keep queued and retry soon
        schedule(convId, 250, dispatch);
        return;
      }
      // Drop on non-409 error
      dispatch(dequeue({ convId }));
      const userFacingError =
        extractErrorMessage(err) ?? 'Technical error encountered while sending your message.';
      dispatch(
        addEntity({
          convId,
          entity: {
            id: `error-${Date.now()}`,
            kind: 'message',
            timestamp: Date.now(),
            props: {
              role: 'system',
              content: `⚠️ ${userFacingError}`,
              severity: 'error',
            },
          } as any,
        })
      );
      // Try next item if any
      dispatch(processQueue(convId));
    }
  };

function extractErrorMessage(err: unknown): string | null {
  if (!err) {
    return null;
  }
  if (typeof err === 'string') {
    return err;
  }
  if (typeof (err as any)?.data?.error === 'string') {
    return (err as any).data.error;
  }
  if (typeof (err as any)?.error === 'string') {
    return (err as any).error;
  }
  if (typeof (err as any)?.message === 'string') {
    return (err as any).message;
  }
  return null;
}

// Selectors
export const selectQueue = (convId: string) => (state: RootState) =>
  state.chatQueue?.byConvId?.[convId]?.queue ?? [];
export const selectIsProcessing = (convId: string) => (state: RootState) =>
  state.chatQueue?.byConvId?.[convId]?.processing ?? false;
export const selectPendingStreamRun = (convId: string | null) => (state: RootState) =>
  (convId && state.chatQueue?.byConvId?.[convId]?.pendingStreamRun) || null;
